import React from "react";
import hero from "@content/hero.json";
import mentor from "@content/mentor.json";
import stats from "@content/stats.json";
import settings from "@content/settings.json";
import PacketTopology from "./PacketTopology";

const Hero = ({ onReserve }) => {
    return (
        <section className="relative overflow-hidden pt-20 md:pt-24 pb-10 md:pb-14" data-testid="hero-section">
            <div className="absolute inset-0 bg-grid pointer-events-none" aria-hidden />
            <div
                className="absolute inset-x-0 top-0 h-[500px] pointer-events-none"
                style={{
                    background:
                        "radial-gradient(600px 380px at 20% 20%, rgba(124,231,59,0.10), transparent 60%)",
                }}
                aria-hidden
            />

            <div className="container-p relative">
                {/* Live batch badge */}
                <div className="flex items-center justify-start mb-4">
                    <div
                        className="inline-flex items-center gap-2 rounded-full pl-2 pr-4 py-1.5 border border-[rgba(124,231,59,0.28)] bg-[rgba(124,231,59,0.05)]"
                        data-testid="hero-live-badge"
                    >
                        <span className="pulse-dot" />
                        <span className="text-xs md:text-sm font-mono text-brand tracking-wide">
                            {hero.badge}
                        </span>
                    </div>
                </div>

                <div className="grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-14 items-start">
                    {/* Left column */}
                    <div>
                        <h1
                            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-slate-900"
                            data-testid="hero-headline"
                        >
                            {hero.headline.split(".").map((part, i, arr) => (
                                <span key={i}>
                                    {i === 1 ? (
                                        <span className="text-brand">{part}.</span>
                                    ) : (
                                        <>{part}{i < arr.length - 1 ? "." : ""}</>
                                    )}
                                </span>
                            ))}
                        </h1>

                        <p
                            className="mt-4 text-base md:text-lg text-dim max-w-[620px] leading-relaxed"
                            data-testid="hero-subheadline"
                        >
                            {hero.subheadline}
                        </p>

                        {/* Trainer strip */}
                        <div className="mt-5 flex items-center gap-3">
                            <img
                                src={mentor.image}
                                alt={`${mentor.name}, ${mentor.role}, live workshop instructor`}
                                width="44"
                                height="44"
                                className="w-11 h-11 rounded-full object-cover border border-[rgba(124,231,59,0.3)]"
                                loading="lazy"
                                decoding="async"
                                data-testid="hero-trainer-avatar"
                            />
                            <div className="font-mono text-sm text-dim">
                                Live with{" "}
                                <span className="text-slate-900 font-semibold">{mentor.name}</span>{" "}
                                <span className="text-brand">({mentor.nickname})</span>
                            </div>
                        </div>

                        {/* CTAs */}
                        <div className="mt-6 flex flex-wrap items-center gap-3">
                            <a
                                href={settings.razorpay_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-primary text-base md:text-lg"
                                data-testid="hero-cta-primary"
                                onClick={onReserve}
                            >
                                <span>{hero.cta}</span>
                                <span aria-hidden>→</span>
                            </a>
                            <a
                                href="#roadmap"
                                className="btn-ghost text-base"
                                data-testid="hero-cta-secondary"
                            >
                                {hero.cta_secondary}
                            </a>
                        </div>

                        {/* Trust bullets */}
                        <ul className="mt-6 grid grid-cols-2 gap-2 max-w-[560px]" data-testid="hero-trust-bullets">
                            {hero.trust_bullets.map((b) => (
                                <li
                                    key={b}
                                    className="flex items-center gap-2 text-sm text-dim"
                                >
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        className="text-brand shrink-0"
                                        aria-hidden
                                    >
                                        <path
                                            fill="currentColor"
                                            d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
                                        />
                                    </svg>
                                    {b}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Right column: packet topology */}
                    <div className="relative">
                        <PacketTopology />
                    </div>
                </div>

                {/* Stats row */}
                <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="hero-stats">
                    {hero.stats.map((s) => (
                        <div key={s.label} className="panel px-4 py-4">
                            <div className="font-display text-2xl md:text-3xl font-bold text-slate-900">
                                {s.value}
                            </div>
                            <div className="mt-1 text-xs md:text-sm text-dim font-mono uppercase tracking-wider">
                                {s.label}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Hero;
